import Lottie from 'lottie-react';
import {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useFindProduct, usePaymentHistory} from '@/api/payments';
import {CouponEntryCard} from '@/components/Coupon';
import {ACTIVE_TIER, TRIAL_DURATION} from '@/constants';
import {BillingType, ProductTier, ProductType} from '@/enums';
import {useCheckAccessRestriction, useSession} from '@/hooks';
import {useGA4WithParams} from '@/hooks/tracking';
import {ProductWithPriceInfo} from '@/interface';
import {paths} from '@/router';
import {
  ColumnFlexBox,
  Flexbox,
  FlexCenter,
  FlexFullGrow,
  PaddingBox,
  PageContent,
} from '@/styled-components';
import {MEDIA_QUERIES, palette} from '@/styles';
import {getPrice, withComma} from '@/utils';
import {Button} from '../Button';
import {Checkbox} from '../Checkbox';
import {Modal} from '../Modal';
import {Typography} from '../Typography';
import HeartLottie from './heart3.json';
import {OrderSheet} from './OrderSheet';
import {PayWidgetOrderSheet} from './PayWidgetOrderSheet';
import {PricingNote} from './PricingNote';
import {TrialModal} from './TrialModal';

export const PricingTable = () => {
  const {hasSession, user, isPaidUser} = useSession();
  const {pathname, search} = useLocation();
  const navigate = useNavigate();
  const [startPayment, setStartPayment] = useState<boolean>(false);
  const [showOneDayConfirm, setShowOneDayConfirm] = useState<boolean>(false);

  const {data, isError, isSuccess, isPending} = useFindProduct(user?.id);

  const products: ProductWithPriceInfo[] | undefined = useMemo(() => {
    if (!data) {
      return;
    }

    return data
      .filter((i) => ACTIVE_TIER.includes(i.tier) && i.isActive)
      .map((i) => ({
        ...i,
        priceInfo: getPrice(i),
      }));
  }, [data]);

  const subscriptionPlans = useMemo(() => {
    if (products) {
      return products.filter((i) => i.type === ProductType.Subscription);
    }
  }, [products]);

  const oneTimeProducts = useMemo(() => {
    if (products) {
      return products.filter((i) => i.type === ProductType.OneTime);
    }
  }, [products]);

  const hasProducts = !!products?.length;

  const {data: history} = usePaymentHistory(user?.id);

  const [selectedProduct, setSelectedProduct] =
    useState<ProductWithPriceInfo>();

  const hasPayHistory = !isPaidUser && history && history.length > 0;
  const isTrial = selectedProduct?.tier === ProductTier.Trial;
  const isOneDayProduct =
    selectedProduct?.type === ProductType.OneTime &&
    selectedProduct?.billingType === BillingType.Daily &&
    !!selectedProduct?.name.startsWith('1일');
  const maximumDiscountRate = useMemo(
    () =>
      products
        ? Math.max(...products.map((i) => i.priceInfo.discountRate || 0))
        : 0,
    [products],
  );

  const tracking = useGA4WithParams();
  const {checkAccessRestriction, isChecking} = useCheckAccessRestriction();

  const onClick = async () => {
    if (!hasSession) {
      navigate(paths.signin, {
        state: {
          from: {pathname, search},
        },
      });
    } else if (selectedProduct) {
      if (await checkAccessRestriction()) {
        return;
      }

      if (isOneDayProduct) {
        setShowOneDayConfirm(true);
      } else {
        setStartPayment(true);
        tracking('subscribe_start_click', {
          product_name: selectedProduct.name,
        });
      }
    }
  };

  const handleConfirmOneDayPurchase = () => {
    setShowOneDayConfirm(false);
    if (selectedProduct) {
      setStartPayment(true);
      tracking('subscribe_start_click', {
        product_name: selectedProduct.name,
      });
    }
  };

  const onChangeProduct = (selected: ProductWithPriceInfo) => {
    setSelectedProduct(selected);
  };

  const clearOrderSheet = () => {
    setStartPayment(false);
  };

  useEffect(() => {
    if (!selectedProduct && subscriptionPlans?.length) {
      setSelectedProduct(subscriptionPlans[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionPlans]);

  useEffect(() => {
    if (products && user?.billing?.productId) {
      const selected = products.find((i) => i.id === user?.billing?.productId);

      if (selected) {
        setSelectedProduct(selected);
      } else if (subscriptionPlans?.length) {
        setSelectedProduct(subscriptionPlans[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.billing?.productId, products]);

  if (isPending) {
    return null;
  } else if (isError || (isSuccess && !hasProducts)) {
    return (
      <Root>
        <PaddingBox bottom={24}>
          <ColumnFlexBox gap={8}>
            <Typography variant="bold24" align="center">
              상품 정보를 불러오지 못했습니다.
            </Typography>
            <Typography variant="medium14" align="center">
              이용에 불편을 드려 죄송합니다.
            </Typography>
          </ColumnFlexBox>
        </PaddingBox>
      </Root>
    );
  } else if (!hasProducts) {
    return null;
  }

  const disabled = isPaidUser || !selectedProduct;

  return (
    <Root>
      <PricingSection>
        {!isPaidUser && hasPayHistory && (
          <LimitedWidthContent style={{padding: '0 16px'}}>
            <FlexCenter>
              <TopRoundedInfoBox>
                <Lottie
                  animationData={HeartLottie}
                  style={{
                    width: 44,
                    height: 44,
                  }}
                />
                <div>
                  다시 찾아주셔서 감사합니다. 오늘 요금제를{' '}
                  <Typography color={palette.red100}>재결제</Typography>해
                  보세요.
                </div>
              </TopRoundedInfoBox>
            </FlexCenter>
          </LimitedWidthContent>
        )}
        <Content>
          <FlexCenter>
            <TitleSection>
              <PageTitle>
                <span className="desktop-text">
                  원하는 기간의 이용권을 선택해 보세요!
                </span>
                <span className="mobile-text">요금제 안내</span>
              </PageTitle>
              {maximumDiscountRate > 0 && (
                <SubTitle>
                  최대 {maximumDiscountRate}% 할인 혜택을 놓치지 마세요.
                </SubTitle>
              )}
            </TitleSection>
          </FlexCenter>
          <RoundedBox>
            {subscriptionPlans?.map((plan) => (
              <ProductItemWrapper key={plan.id}>
                <ProductItem
                  plan={plan}
                  selected={selectedProduct?.id === plan.id}
                  disabled={disabled}
                  onClick={onChangeProduct}
                />
                {plan.billingType === BillingType.Monthly && (
                  <CalcBox>
                    <Typography color={palette.black} fontWeight={600}>
                      💡 {plan.name} 구독 할인
                    </Typography>
                    <CalcContent>
                      <Flexbox>
                        상품 가격
                        <FlexFullGrow />
                        {withComma(plan.priceInfo.originalPrice)}원
                      </Flexbox>
                      <Flexbox>
                        할인 가격
                        <FlexFullGrow />-
                        {withComma(plan.priceInfo.discountedPrice)}원
                      </Flexbox>
                      <CalcDivider />
                      <Flexbox alignItems="flex-start">
                        최종 결제 금액
                        <FlexFullGrow />
                        <Typography align="right" as="div">
                          {withComma(plan.priceInfo.finalPrice)}원
                          {!!plan.priceInfo.finalPrice && (
                            <Typography
                              color={palette.gray600}
                              variant="medium12"
                              as="div"
                            >
                              (
                              {withComma(
                                Math.round(plan.priceInfo.finalPrice / 4),
                              )}
                              원 * 4주)
                            </Typography>
                          )}
                        </Typography>
                      </Flexbox>
                    </CalcContent>
                  </CalcBox>
                )}
              </ProductItemWrapper>
            ))}
            {oneTimeProducts?.map((plan) => (
              <ProductItemWrapper key={plan.id}>
                <ProductItem
                  plan={plan}
                  selected={selectedProduct?.id === plan.id}
                  disabled={disabled}
                  onClick={onChangeProduct}
                />
              </ProductItemWrapper>
            ))}
            <ProductItemWrapper>
              <Flexbox>
                <ProductRow>
                  {selectedProduct?.type === ProductType.Subscription ? (
                    <InfoText>
                      {selectedProduct.name}은 자동 결제로 언제든지 구독 해지
                      가능합니다.
                    </InfoText>
                  ) : isOneDayProduct ? (
                    <InfoText>
                      1일 이용권은 계정당 최소 1회만 구매 가능합니다.
                    </InfoText>
                  ) : (
                    <FlexFullGrow />
                  )}
                  <StyledButton
                    size="mediumLarge"
                    color="primary"
                    variant="fill"
                    className={isPaidUser ? 'pressed' : ''}
                    width={232}
                    disabled={disabled || isChecking}
                    onClick={onClick}
                  >
                    {isPaidUser
                      ? '현재 이용 중'
                      : hasSession && isTrial
                        ? `${TRIAL_DURATION}일 무료 체험 시작하기`
                        : selectedProduct?.type === ProductType.Subscription
                          ? `${selectedProduct.name} 구독하기`
                          : `${selectedProduct?.name || '이용권'} 구매하기`}
                  </StyledButton>
                </ProductRow>
              </Flexbox>
            </ProductItemWrapper>
          </RoundedBox>
          <CouponEntryCard />
        </Content>
      </PricingSection>
      <Content>
        <PricingNote
          isTrial={isTrial}
          productType={selectedProduct?.type}
        />
      </Content>
      {showOneDayConfirm && (
        <Modal
          title="구매 제한 안내"
          width={368}
          onClose={() => setShowOneDayConfirm(false)}
        >
          <PaddingBox spacing={16}>
            <Typography as="div" variant="medium14" color={palette.gray600}>
              본 상품은 ID당 1회만 구매 가능한 상품입니다.
              <br />
              계속하시겠습니까?
            </Typography>
          </PaddingBox>
          <PaddingBox spacing={24}>
            <Button
              variant="fill"
              size="medium"
              color="primary"
              fullWidth
              onClick={handleConfirmOneDayPurchase}
            >
              확인
            </Button>
          </PaddingBox>
        </Modal>
      )}
      {!!selectedProduct &&
        startPayment &&
        (isTrial ? (
          <TrialModal
            duration={TRIAL_DURATION}
            item={selectedProduct}
            onClose={clearOrderSheet}
          />
        ) : selectedProduct.type === ProductType.Subscription ? (
          <OrderSheet item={selectedProduct} onClose={clearOrderSheet} />
        ) : (
          <PayWidgetOrderSheet
            item={selectedProduct}
            onClose={clearOrderSheet}
          />
        ))}
    </Root>
  );
};

const ProductItem = ({
  plan,
  selected,
  disabled,
  onClick,
}: {
  plan: ProductWithPriceInfo;
  selected?: boolean;
  disabled?: boolean;
  onClick(product: ProductWithPriceInfo): void;
}) => {
  const onChangeProduct = (e: ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target;

    if (checked) {
      onClick(plan);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      onClick(plan);
    }
  };

  return (
    <ProductRow
      style={{cursor: disabled ? 'default' : 'pointer'}}
      onClick={handleClick}
    >
      <Flexbox gap="12px">
        <Checkbox
          checkType="radio"
          variant="large"
          disabled={disabled}
          name={plan.id}
          checked={selected}
          onChange={onChangeProduct}
        />
        <Flexbox gap="8px">
          <ProductMainText>{plan.name}</ProductMainText>
          <Badges>
            {plan.badge?.map((i, idx) => (
              <div key={idx} dangerouslySetInnerHTML={{__html: i}} />
            ))}
          </Badges>
        </Flexbox>
      </Flexbox>
      <ColumnFlexBox gap={4}>
        <Flexbox gap="8px">
          {!!plan.priceInfo.discountRate && (
            <DiscountBadge>{plan.priceInfo.discountRate}% 할인</DiscountBadge>
          )}
          <ProductMainText>
            {withComma(plan.priceInfo.finalPrice)}원
          </ProductMainText>
        </Flexbox>
        <Typography variant="medium14" color={palette.gray500} fontWeight={400}>
          <Flexbox gap="8px" justifyContent="flex-end">
            {!!plan.priceInfo.originalPrice && (
              <StrikeText>
                {withComma(plan.priceInfo.originalPrice)}원
              </StrikeText>
            )}
            (VAT 포함)
          </Flexbox>
        </Typography>
      </ColumnFlexBox>
    </ProductRow>
  );
};

const Root = styled.div`
  width: 100%;
  padding-bottom: 60px;
`;

const PricingSection = styled.div`
  padding-top: 20px;
  background: linear-gradient(to bottom, #f6f0e7 50%, transparent 50%);
`;

const RoundedBox = styled.div`
  border-radius: 20px;
  background-color: ${palette.white};
  box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.1);
`;

const ProductItemWrapper = styled.div`
  padding: 24px 28px;
  & + & {
    border-top: solid 1px ${palette.gray100};
  }
  ${MEDIA_QUERIES.mobile} {
    padding: 20px 16px;
  }
`;

const TitleSection = styled.div`
  line-height: 1.8;
  color: ${palette.gray900};
  text-align: center;
  margin-bottom: 28px;
`;

const PageTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  .mobile-text {
    display: none;
  }
  ${MEDIA_QUERIES.mobile} {
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: inline;
    }
  }
`;

const SubTitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  ${MEDIA_QUERIES.mobile} {
    display: none;
  }
`;

const ProductMainText = styled.div`
  color: ${palette.gray900};
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
  ${MEDIA_QUERIES.mobile} {
    font-size: 15px;
  }
`;

const ProductRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;
  ${MEDIA_QUERIES.mobile} {
    flex-direction: column;
    align-items: flex-start;
    & > *:last-child {
      align-self: flex-end;
    }
  }
`;

const StrikeText = styled(Typography)`
  text-decoration: line-through;
`;

const InfoText = styled.div`
  color: ${palette.gray600};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
`;

const LimitedWidthContent = styled(PageContent)`
  max-width: 632px;
`;

const Content = styled(LimitedWidthContent)`
  padding: 20px 16px;
`;

const Badge = styled.div`
  display: flex;
  padding: 2px 4px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DiscountBadge = styled(Badge)`
  background-color: ${palette.primary100};
  color: ${palette.teal100};
`;

const CalcBox = styled.div`
  display: flex;
  padding: 24px;
  justify-content: space-between;
  border-radius: 4px;
  background: #f8fafb;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  margin-top: 20px;
  ${MEDIA_QUERIES.mobile} {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }
`;

const CalcContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  color: ${palette.gray800};
  gap: 4px;
  ${MEDIA_QUERIES.mobile} {
    width: 100%;
  }
`;

const CalcDivider = styled.div`
  margin: 8px 0;
  height: 1px;
  width: 100%;
  background-color: ${palette.gray800};
`;

const TopRoundedInfoBox = styled(Flexbox)`
  background-color: ${palette.white};
  border-radius: 999px;
  box-shadow:
    0px 4px 8px 0px rgba(0, 0, 0, 0.1),
    0px -2px 4px 0px rgba(0, 0, 0, 0.05) inset;
  padding: 6px 34px 6px 8px;
  color: ${palette.gray800};
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  gap: 8px;
  margin: 8px 0;
  ${MEDIA_QUERIES.mobile} {
    padding: 6px 16px 6px 8px;
    font-size: 13px;
    border-radius: 16px;
  }
`;

const StyledButton = styled(Button)`
  height: 50px;
  font-size: 14px;
  font-weight: 600;
  ${MEDIA_QUERIES.mobile} {
    width: 100%;
    max-width: 100%;
  }
  &.pressed {
    border-radius: 10px;
    background: ${palette.gray150};
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.2) inset;
    border-color: transparent;
    color: ${palette.gray600};
    opacity: 1;
  }
`;
